import { useState } from 'react';
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { TabList, Tabs, TabTrigger } from '@components/main-components/tabs/Tab';
import { ModalContext } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, PlaylistShareInfoFragmentDoc } from '@graphql/reactQuery';
import { useCopy } from '@hooks/useCopy';
import { usePrefetchEmbed } from '@hooks/usePrefetchEmbed';
import { KeepCurrentBrowserHost } from '@utils/url';
import { EmbedBody } from './EmbedBody';
import { SocialShare } from './SocialShare';
import { UrlCopy } from './UrlCopy';
import { SHARE_TRANSITION, ShareTab, type ShareTabKey } from './helpers';

export function ShareModalPlaylist({
  playlist,
}: {
  playlist: FragmentType<PlaylistShareInfoFragmentDoc> & { id: string };
}) {
  const { closeCancelModal } = ModalContext.useContainer();

  const playlistInfo = getFragment(PlaylistShareInfoFragmentDoc, playlist);

  const playlistUrl = KeepCurrentBrowserHost(playlistInfo.webappUri).href;
  const [activeTab, setActiveTab] = useState<ShareTabKey>('social');

  usePrefetchEmbed({
    playlistId: playlistInfo.id,
  });

  const { copy: copyPlaylistUrl, isCopied } = useCopy({
    text: playlistUrl,
    successMessage: 'Link copied to clipboard',
  });

  const body = (
    <>
      <AnimatePresence initial={false} mode="popLayout">
        <Tabs
          defaultValue={ShareTab.social.key}
          value={activeTab}
          onValueChange={v => setActiveTab(v as ShareTabKey)}
        >
          <TabList>
            <TabTrigger value={ShareTab.social.key} label="Social Share" className="normal-case" />
            <TabTrigger value={ShareTab.embed.key} label="Embed" className="normal-case" />
          </TabList>
        </Tabs>
        {activeTab === ShareTab.social.key && (
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            key={ShareTab.social.key}
            transition={SHARE_TRANSITION}
            className="flex flex-col gap-6 pt-6"
          >
            <SocialShare
              associationType="Playlist"
              id={playlistInfo.id}
              url={playlistUrl}
              messageType="playlist"
              referralAddress={null}
            />
            <UrlCopy url={playlistUrl} isCopied={isCopied} onCopyClick={copyPlaylistUrl} />
          </motion.div>
        )}
        {activeTab === ShareTab.embed.key && (
          <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%' }}
            key={ShareTab.embed.key}
            transition={SHARE_TRANSITION}
          >
            <EmbedBody type="playlist" playlistId={playlistInfo.id} className="mb-0.5 pt-3" />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  return (
    <StandardModal
      body={body}
      title="Share"
      closeModal={closeCancelModal}
      bodyClassName="overflow-x-hidden"
      bodyWithoutPaddingBottom
      mobileFullScreen
    />
  );
}
